import { ReactNode, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useSnapshot } from 'valtio';

import Text from '@cartwhl/ui/base/Text';
import FileIcon from '@cartwhl/ui/icons/FileIcon';

import { animationGenerationStore, navigationStore } from 'stores';

import { MAX_FILE_SIZE, VIDEO_EXTENSIONS } from '../constants';
import { getFileExtensions } from '../utils';

const accept = { 'application/octet-stream': VIDEO_EXTENSIONS };

export default function VideoFilePickerCompact({
  children,
}: {
  children?: ReactNode;
}) {
  const { videoUpload } = useSnapshot(animationGenerationStore.state);
  const [fileError, setFileError] = useState<string | null>(null);

  const handleDrop = async (acceptedFiles: File[], rejectedFiles: any[]) => {
    if (rejectedFiles.length > 0) {
      setFileError(
        `Invalid file type. Please upload a ${getFileExtensions(accept)} file.`,
      );
      return;
    }

    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      if (file.size > MAX_FILE_SIZE) {
        setFileError(
          `File size must be less than ${MAX_FILE_SIZE / (1024 * 1024)}MB`,
        );
        return;
      }
      setFileError(null);

      console.log(file);
      navigationStore.setVideoUploadModal({
        mode: 'trimming start',
        initialSourceVideoBlob: file,
      });
    }
  };

  const { getRootProps, getInputProps, isDragActive, inputRef } = useDropzone({
    onDrop: handleDrop,
    accept,
    maxFiles: 1,
    multiple: false,
    disabled: videoUpload && videoUpload.status !== 'ERROR',
  });

  return (
    <div
      {...getRootProps()}
      className={`border-blue relative flex h-full min-h-24 w-full items-center justify-center overflow-hidden rounded-[inherit] border border-dashed p-4 transition-colors duration-200 ease-in-out dark:text-white
          ${isDragActive ? 'bg-blue/20' : 'bg-blue/10 hover:bg-blue/20'}
          ${videoUpload ? 'cursor-default' : 'cursor-pointer'}
        `}
    >
      <input {...getInputProps()} disabled={Boolean(videoUpload)} />
      {children ? (
        children
      ) : (
        <div className="flex h-full items-center justify-center">
          <div className="flex items-center gap-1.5">
            <FileIcon className="text-blue" />
            <Text variant="studio/body/medium/sm">
              <span className="text-blue">Select File</span>{' '}
              <span className="hidden md:inline">
                or drag and drop one here
              </span>
            </Text>
          </div>
        </div>
      )}
    </div>
  );
}
